import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useFragment } from "react-relay";

import { useJoinBookingAndNavigate } from "@/features/JoinBookingModal";
import { teeSheetRequestBookingInfoAtom } from "@/features/TeeSheet/atoms";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { Button } from "@whoosh/design-system/src/components/controls";
import { JoinWaitlistIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { VStack } from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";

import { useFlags } from "../../../../utils/launchDarkly";
import { WaitlistBodyFragment$key } from "./__generated__/WaitlistBodyFragment.graphql";
import { WaitlistBookings } from "./components/WaitlistBookings";

type WaitlistBodyProps = {
  fragmentRef: WaitlistBodyFragment$key | null;
};

export const WaitlistBody = ({ fragmentRef }: WaitlistBodyProps) => {
  const data = useFragment(WaitlistBodyFragmentString, fragmentRef);
  const agenda = data?.agendas?.edges ? data?.agendas?.edges[0]?.node : null;

  const trackEvent = useTrackEvent();

  const setRequestBookingInfo = useSetAtom(teeSheetRequestBookingInfoAtom);
  const { joinBooking: joinWaitlistBooking } = useJoinBookingAndNavigate();

  const handleCreateNewBookingRequest = (
    queueSlotId?: string,
    requestedTime?: string
  ) => {
    if (!queueSlotId) {
      return;
    }
    setRequestBookingInfo({
      queueSlotId,
      requestedTime: requestedTime ? requestedTime : null,
    });
  };

  const supportsPublic = agenda?.facility.club?.supportsPublic ?? false;

  const { showToast } = useToast();

  const { waitlistRestrictions } = useFlags();

  const hasWaitlistRestrictions =
    !!data?.agendas?.edges?.[0]?.node?.waitlistRestrictions?.edges?.length;

  const handleJoinWaitlist = () => {
    const queueSlotId = agenda?.queueSlot?.id;
    if (!queueSlotId) {
      showToast({
        title: t`Waitlist not available`,
        description: t`Please contact the club for more information`,
        semanticStyle: "error",
      });
      return;
    }

    trackEvent("Agenda.WaitlistJoinButtonClicked", {
      queueSlotId,
    });
    handleCreateNewBookingRequest(queueSlotId);
  };

  const isMobileScreen = useBreakpointValue({
    base: true,
    xs: true,
    sm: true,
    md: true,
    agendaSplit: false,
    eventCardColumn: false,
    lg: false,
    xl: false,
  });

  return (
    <VStack gap={2}>
      {hasWaitlistRestrictions && waitlistRestrictions && !isMobileScreen ? (
        <Button
          variant="white"
          accessibilityLabel={t`Join Waitlist button`}
          leftIcon={<JoinWaitlistIcon />}
          onPress={handleJoinWaitlist}
        >
          {t`Join Waitlist`}
        </Button>
      ) : null}

      {!supportsPublic && agenda ? (
        <WaitlistBookings
          agendaRef={agenda}
          onWaitlistBookingPress={joinWaitlistBooking}
        />
      ) : null}
    </VStack>
  );
};

export const WaitlistBodyFragmentString = graphql`
  fragment WaitlistBodyFragment on Facility {
    agendas(dateGte: $date, dateLte: $date) {
      edges {
        node {
          id
          ...WaitlistBookings_Agenda

          facility {
            club {
              supportsPublic
            }
          }
          waitlistRestrictions: bookingRules(
            type: [WAITLIST]
            courseIds: $courseIds
          ) {
            edges {
              liftAt
              node {
                id
              }
            }
          }
          queueSlot {
            id
          }
        }
      }
    }
  }
`;
